import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { Shell } from '../components/Shell';
import Loader from '../components/Loader';
import { fetchCoverage, buildCoverageChips, nextStepFor } from '../components/EntryFlowModal';
import { MonthLedger } from '../components/MonthLedger';
import { X } from '../components/v2/Icons';
import { EntryStepper } from '../components/v2/EntryStepper';
import { CategoryContext } from '../components/v2/CategoryContext';
import { PageHeader, Card, Button, Field, Input, Select, UnitInput, Callout } from '../components/v2/ui';
import { useT } from '../i18n';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Module-level labels: resolved via stored language, not the hook
const isEn = () => localStorage.getItem('k360-lang') === 'en';
const PROCESS_TYPES = [
  { value: 'prodhimi', label: isEn() ? 'Production' : 'Prodhimi' },
  { value: 'perpunimi', label: isEn() ? 'Processing' : 'Përpunimi' },
  { value: 'ngrohja', label: isEn() ? 'Heating' : 'Ngrohja' },
  { value: 'ftohja', label: isEn() ? 'Cooling' : 'Ftohja' }
];

export const Scope2ProcessPage = () => {
  const navigate = useNavigate();
  const { t } = useT();
  const [formData, setFormData] = useState({
    buildingId: '',
    processType: '',
    fuelKey: '',
    period: getCurrentMonth(),
    quantity: '205',
    unit: 'l'
  });

  const [facilities, setFacilities] = useState([]);
  const [fuels, setFuels] = useState([]);
  // Verifiko → Ruaj → Vazhdo dialog
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState('manual');
  const [flowError, setFlowError] = useState('');
  const [coverage, setCoverage] = useState(null);
  const [ledgerKey, setLedgerKey] = useState(0);
  // Evidence upload (stored in the invoice register, linked to the record)
  const [uploadedInvoice, setUploadedInvoice] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docUrl, setDocUrl] = useState(null);
  useEffect(() => {
    if (!uploadedFile) { setDocUrl(null); return undefined; }
    const url = URL.createObjectURL(uploadedFile);
    setDocUrl(url);
    return () => URL.revokeObjectURL(url); // avoid leaking blob URLs across uploads
  }, [uploadedFile]);
  const [calculated, setCalculated] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/buildings`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => setFacilities(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error('Failed to fetch facilities:', err);
        setFacilities([]);
      });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/fuels`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => setFuels(Array.isArray(data) ? data : []))
      .catch(err => console.error('Failed to fetch fuels:', err));
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsSaved(false);
  };

  const handleCalculate = async () => {
    try {
      setCalculating(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/emissions/scope2/process/calc`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          buildingId: parseInt(formData.buildingId),
          processType: formData.processType,
          fuelKey: formData.fuelKey,
          period: formData.period,
          quantity: parseFloat(formData.quantity),
          unit: formData.unit
        })
      });

      if (!response.ok) throw new Error('Calculation failed');
      const data = await response.json();
      setCalculated(data);
      setIsSaved(false);
      // Verifiko → Ruaj → Vazhdo: open the dialog right after the calculation
      setFlowError('');
      setStep(2);
    } catch (err) {
      alert(t('invnp.errorWithMessage', { message: err.message }));
    } finally {
      setCalculating(false);
    }
  };

  const handleSave = async () => {
    if (!calculated) return false;
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      if (!token) {
        alert(t('invp.sessionExpired'));
        window.location.href = '/';
        return false;
      }
      
      const response = await fetch(`${API_BASE}/api/emissions/scope2/process/records`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          buildingId: parseInt(formData.buildingId),
          processType: formData.processType,
          fuelKey: formData.fuelKey,
          period: formData.period,
          quantity: parseFloat(formData.quantity),
          unit: formData.unit,
          energy_kWh: calculated.energy_kWh,
          emissions_tCO2e: calculated.emissions_tCO2e,
          invoiceId: uploadedInvoice ? uploadedInvoice.id : null
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.errors ? Object.values(errorData.errors).join('; ') : '';
        throw new Error(detail || errorData.message || 'Save failed');
      }
      setIsSaved(true);
      return true;
    } catch (err) {
      // Surface the real reason inside the dialog instead of a browser alert
      setFlowError(t('invnp.errorWithMessage', { message: err.message }));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Evidence upload → invoice register; linked to the record on save
  /** Takes the file directly — the stepper's drop zone supplies it. */
  const handleUploadInvoice = async (picked) => {
    if (!picked) return;
    if (!formData.buildingId) { setFlowError(t('entry.uploadNoBuilding')); return; }
    if (picked.size > 10 * 1024 * 1024) { setFlowError(t('entry.uploadTooBig')); return; }
    try {
      setUploading(true);
      setFlowError('');
      const fd = new FormData();
      fd.append('file', picked);
      fd.append('buildingId', formData.buildingId);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/invoices`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Upload failed');
      }
      setUploadedInvoice(await res.json());
      setUploadedFile(picked);
    } catch (err) {
      setFlowError(t('entry.uploadFailed', { message: err.message }));
    } finally {
      setUploading(false);
    }
  };

  const handleFlowSave = async () => {
    setFlowError('');
    const ok = await handleSave();
    if (!ok) {
      setFlowError(prev => prev || t('invp.saveRetry'));
      return;
    }
    try {
      setCoverage(await fetchCoverage(formData.period));
    } catch (e) {
      setCoverage(null);
    }
    setLedgerKey(k => k + 1);
  };

  const handleFlowAddAnother = () => {
    setStep(1);
    setCalculated(null);
    setIsSaved(false);
    // Clear the attached document too — otherwise the previous bill would be
    // linked to (and shown with) the next, unrelated record
    setUploadedInvoice(null);
    setUploadedFile(null);
    setFormData(prev => ({ ...prev, processType: '', fuelKey: '', quantity: '' }));
  };

  const handleFlowFinish = () => setStep(0);

  const isCalculateDisabled = !formData.buildingId || !formData.processType || !formData.fuelKey || 
    !formData.period || !formData.quantity || parseFloat(formData.quantity) <= 0 || calculating;

  const isBusy = calculating || saving;

  const selectedType = PROCESS_TYPES.find(p => p.value === formData.processType);
  const selectedFuel = fuels.find(f => (f.key || f.value) === formData.fuelKey);

  return (
    <ErrorBoundary>
      <Shell>
        <div className="k2-wrap">
          {isBusy && <Loader />}

          <PageHeader title={t('heat.title')} desc={t('heat.desc')}>
            <Button variant="gh" icon={X.Doc} onClick={() => navigate('/records')}>{t('entry.viewRecords')}</Button>
          </PageHeader>

          <EntryStepper
            scope={2}
            step={step}
            setStep={setStep}
            facilities={facilities}
            siteId={formData.buildingId}
            onSite={(e) => handleInputChange('buildingId', e.target.value)}
            /* processType (Prodhimi / Përpunimi / Ngrohja / Ftohja) already answers
               "what is this energy for", so a second purpose selector would ask the
               same question twice — even though the column exists on this table. */
            purposes={[]}
            period={formData.period}
            mode={mode}
            setMode={setMode}
            ocrSupported={false}
            docLabel={t('heat.doc')}
            file={uploadedFile}
            onFile={handleUploadInvoice}
            uploading={uploading}
            uploadNote={uploadedInvoice ? (
              <div style={{ marginTop: 10 }}><Callout tone="ok">{t('entry.uploadOk')}</Callout></div>
            ) : null}
            onCalculate={handleCalculate}
            calcDisabled={isCalculateDisabled}
            calcLabel={calculating ? t('entry.calculating') : t('entry.calc')}
            reviewRows={[
              { label: t('entry.building'), value: (facilities.find(f => String(f.id) === String(formData.buildingId)) || {}).name || '—' },
              { label: t('heat.type'), value: (selectedType || {}).label || formData.processType || '—' },
              { label: t('heat.fuel'), value: (selectedFuel || {}).label || formData.fuelKey || '—' },
              { label: t('stationary.quantity'), value: `${formData.quantity} ${formData.unit}` },
              { label: t('entry.period'), value: formData.period },
              { label: 'kWh', value: calculated ? `${Number(calculated.energy_kWh || 0).toFixed(0)} kWh` : '—' },
              { label: t('entry.document'), value: uploadedFile ? uploadedFile.name : t('entry.notAttached') }
            ]}
            result={{ value: calculated ? Number(calculated.emissions_tCO2e).toFixed(3) : '0.000', unit: t('common.tco2e') }}
            saving={saving}
            error={flowError}
            onSave={handleFlowSave}
            saved={isSaved}
            successText={calculated
              ? t('heat.success', { value: Number(calculated.emissions_tCO2e).toFixed(3), period: formData.period })
              : ''}
            coverage={buildCoverageChips('scope2', coverage)}
            nextStep={nextStepFor('scope2')}
            onAddAnother={handleFlowAddAnother}
            onFinish={handleFlowFinish}
            context={
              <>
                {step === 2 && docUrl && (
                  <Card title={t('elec.sourceDoc')} sub={uploadedFile ? uploadedFile.name : ''} bodyProps={{ style: { padding: 0 } }}>
                    <div style={{ borderTop: '1px solid var(--n200)', maxHeight: 420, overflow: 'auto' }}>
                      {uploadedFile && uploadedFile.type === 'application/pdf' ? (
                        <iframe title={t('elec.sourceDoc')} src={docUrl} style={{ width: '100%', height: 420, border: 0 }} />
                      ) : (
                        <img alt={t('elec.sourceDoc')} src={docUrl} style={{ width: '100%', display: 'block' }} />
                      )}
                    </div>
                  </Card>
                )}
                <CategoryContext
                  recordTypes={['scope2-process']}
                  categoryLabel={t('heat.title')}
                  facilities={facilities}
                  refreshKey={ledgerKey}
                  onPickSite={(id) => { handleInputChange('buildingId', String(id)); setStep(1); }}
                />
                <MonthLedger
                  period={formData.period}
                  recordTypes={['scope2-process']}
                  buildings={facilities}
                  refreshKey={ledgerKey}
                  emptyHint={t('heat.ledgerEmpty')}
                  onChanged={() => setLedgerKey(k => k + 1)}
                />
              </>
            }
          >
            <div className="k2-g2">
              <Field label={t('heat.type')} required>
                <Select value={formData.processType} onChange={(e) => handleInputChange('processType', e.target.value)}>
                  <option value="">{t('heat.selectType')}</option>
                  {PROCESS_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </Select>
              </Field>

              <Field label={t('heat.fuel')} required>
                <Select value={formData.fuelKey} onChange={(e) => handleInputChange('fuelKey', e.target.value)}>
                  <option value="">{t('heat.selectFuel')}</option>
                  {fuels.map(f => (
                    <option key={f.key || f.value} value={f.key || f.value}>{f.label}</option>
                  ))}
                </Select>
              </Field>
            </div>

            <div className="k2-g2">
              <Field label={t('stationary.startMonth')} required>
                <Input type="month" value={formData.period} onChange={(e) => handleInputChange('period', e.target.value)} />
              </Field>

              <Field label={t('stationary.quantity')} required>
                <UnitInput
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => handleInputChange('quantity', e.target.value)}
                  unit={formData.unit}
                />
              </Field>
            </div>
          </EntryStepper>
        </div>
      </Shell>
    </ErrorBoundary>
  );
};
